export interface ViewportDimensions {
  readonly width: number;
  readonly height: number;
}

const validDimension = (value: number, fallback: number): number =>
  Number.isFinite(value) && value > 0 ? value : fallback;

export const resolveEmbeddedViewport = (
  bridge: ViewportDimensions | undefined,
  browser: ViewportDimensions,
): ViewportDimensions => ({
  // Native MAX/VK chrome is not part of the browser's drawable viewport. The
  // bridge can therefore refine the size downwards, but never expand it.
  width: Math.min(validDimension(bridge?.width ?? 0, browser.width), browser.width),
  height: Math.min(validDimension(bridge?.height ?? 0, browser.height), browser.height),
});

/** Retain agreed bounds until the browser actually changes; ignore stale native replies. */
export const createEmbeddedViewportController = (
  readBrowser: () => ViewportDimensions,
  readBridge: () => Promise<ViewportDimensions | undefined>,
  apply: (viewport: ViewportDimensions) => void,
) => {
  let revision = 0;
  let disposed = false;
  let lastBrowser: ViewportDimensions | undefined;
  let lastApplied: ViewportDimensions | undefined;
  const equal = (a: ViewportDimensions | undefined, b: ViewportDimensions) =>
    a?.width === b.width && a.height === b.height;
  const publish = (size: ViewportDimensions) => {
    if (equal(lastApplied, size)) return;
    lastApplied = size;
    apply(size);
  };
  return {
    async update(force = false): Promise<void> {
      if (disposed) return;
      const browser = readBrowser();
      if (![browser.width, browser.height].every((value) => Number.isFinite(value) && value > 0))
        return;
      const changed = !equal(lastBrowser, browser);
      if (!changed && !force) return;
      const request = ++revision;
      const nativeConstrained =
        lastBrowser &&
        lastApplied &&
        lastBrowser.width === browser.width &&
        lastApplied.height < lastBrowser.height;
      const immediate =
        nativeConstrained && lastApplied ? resolveEmbeddedViewport(lastApplied, browser) : browser;
      lastBrowser = browser;
      if (changed) publish(immediate);
      let bridge: ViewportDimensions | undefined;
      try {
        bridge = await readBridge();
      } catch {
        // Native methods may reject during suspension or on older clients.
      }
      if (disposed || request !== revision) return;
      const current = readBrowser();
      if (![current.width, current.height].every((value) => Number.isFinite(value) && value > 0))
        return;
      // An unavailable bridge must not expand a previously agreed, unchanged viewport.
      if (bridge || !equal(current, browser)) publish(resolveEmbeddedViewport(bridge, current));
    },
    dispose(): void {
      disposed = true;
      revision++;
    },
  };
};
